import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button, Card, PageState, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import {
  useAdminAutomationTemplates,
  useDeleteAutomationTemplate,
} from '../../api/automation-templates';

/** Platform-admin CRUD for the master automation template library (docs/API_SPEC.md §14). */
export function AutomationTemplatesAdminPage() {
  const { isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const templates = useAdminAutomationTemplates();
  const del = useDeleteAutomationTemplate();

  if (!isPlatformAdmin) return <Navigate to="/" replace />;

  async function remove(id: string, name: string) {
    if (!window.confirm(`Delete template "${name}"? This cannot be undone.`)) return;
    try {
      await del.mutateAsync(id);
      toast.success('Template deleted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the template.');
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Automation templates</h1>
        <span style={{ flex: 1 }} />
        <Button onClick={() => navigate('/admin/automation-templates/new')}>New template</Button>
      </div>

      <Card title="Library">
        {templates.isPending ? (
          <PageState kind="loading" message="Loading templates…" />
        ) : templates.isError ? (
          <PageState
            kind="error"
            message={templates.error.message}
            onRetry={() => void templates.refetch()}
          />
        ) : templates.data.items.length === 0 ? (
          <PageState kind="empty" title="No templates yet" message="Create one above." />
        ) : (
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 'var(--space-2)',
            }}
          >
            {templates.data.items.map((t) => (
              <li
                key={t.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  borderBottom: '1px solid var(--color-border)',
                  paddingBottom: 'var(--space-2)',
                }}
              >
                <strong>{t.name}</strong>
                <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                  {t.key}
                </span>
                <span className="ne-badge">{t.category}</span>
                <span className="ne-badge">{t.status}</span>
                {t.isSystem ? <span className="ne-badge">system</span> : null}
                <span style={{ flex: 1, minWidth: 0 }} />
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/admin/automation-templates/${t.id}/edit`)}
                >
                  Edit
                </Button>
                <Button
                  variant="secondary"
                  disabled={del.isPending}
                  onClick={() => void remove(t.id, t.name)}
                >
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/admin/tenants">Tenants</Link> · <Link to="/admin/plans">Plans</Link> ·{' '}
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
